<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<template>
  <div class="additional-info-list">
    <v-row no-gutters class="additional-info-list-info-text">
      <v-col cols="12" class="additional-info-list-i-content">
        <v-icon>fa:fas fa-info-circle</v-icon>
        This section allows you to upload files and add typed notes. For example, additional
        information could include risk assessments provided by external providers.
        <br /><br />
        You can upload one or more file(s) including additional relevant documents such as risk
        assessments or relevant medical information. Alternatively, you can enter in notes in the
        Note section. Add additional files or notes as required by clicking Add New.
      </v-col>
    </v-row>
    <div v-for="(additional, index) in additionalInformationItems" :key="index">
      <div v-if="!additional.isDeleted">
        <div v-if="!additional.notes">
          <v-row>
            <v-col cols="12">
              <input
                :id="getUploadFileId(index)"
                style="display: none"
                type="file"
                :accept="fileTypeAccept"
                @change="handleTemplateFileChange($event, additional, index)"
              />
              <cs-button
                :id="getUploadButtonId(index)"
                :disabled="!formControlEnabled"
                primary
                left-icon="mdi-upload"
                label="Upload"
                class="excursion-stepper-upload-btn"
                @click="uploadFileClick(index)"
              ></cs-button>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-label class="form-label safe-trip-font"> File Name </v-label>
              <div>
                {{ additional.filename }}
                <v-icon
                  v-if="additional.sid"
                  :id="getDownloadFileId(index)"
                  size="medium"
                  style="font-size: 15px; height: 17px; margin-left: 0.7%"
                  @click="downloadFile(additional)"
                  >mdi-download</v-icon
                >
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-label class="form-label safe-trip-font"> Uploaded Date </v-label>
              <div>
                {{ getUploadDate(additional.fileUpdateDate) }}
              </div>
            </v-col>
          </v-row>
        </div>
        <v-row>
          <v-col cols="12">
            <v-label class="form-label safe-trip-font">
              Summary <span v-show="showFirstMandatory(additional)" class="text-red">*</span>
            </v-label>
            <v-text-field
              :id="getFileSummaryId(index)"
              v-model="additional.fileSummary"
              variant="solo"
              placeholder="Summary"
              class="name-textbox safe-trip-font excursion-input"
              :disabled="!formControlEnabled"
              density="compact"
              @update:model-value="additionalValueChange(additional, index)"
            />
          </v-col>
        </v-row>
        <v-row v-if="!additional.notes" no-gutters>
          <v-col cols="12">
            <v-label class="form-label safe-trip-font"> File Description </v-label>
            <v-textarea
              :id="getDescriptionId(index)"
              v-model="additional.comment"
              variant="solo"
              placeholder="Add a file description or add notes without uploading a file"
              rows="4"
              class="textarea-comment excursion-input"
              style="margin-top: 0px"
              :no-resize="true"
              :disabled="!formControlEnabled"
              @change="additionalValueChange(additional, index)"
            ></v-textarea>
          </v-col>
        </v-row>
        <v-row v-if="!additional.filename" no-gutters>
          <v-col cols="12">
            <v-label class="form-label safe-trip-font">
              Notes <span v-show="showFirstMandatory(additional)" class="text-red">*</span>
            </v-label>
            <v-textarea
              :id="getNotesId(index)"
              v-model="additional.notes"
              variant="solo"
              placeholder="Notes"
              rows="4"
              class="textarea-comment excursion-input"
              style="margin-top: 0px"
              :no-resize="true"
              :disabled="!formControlEnabled"
              @update:model-value="additionalValueChange(additional, index)"
            ></v-textarea>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12">
            <cs-button
              v-if="showAddFileButton(index)"
              :id="getAddFileId(index)"
              small
              class="add-additional-btn"
              label="Add New"
              :disabled="!formControlEnabled"
              @click="addAdditionalInfo()"
            ></cs-button>
            <cs-button
              v-if="additional.showDeleteButton"
              :id="getDeleteId(index)"
              small
              delete
              class="delete-additional-btn"
              :class="{ 'continue-btn': !showAddFileButton(index) }"
              label="Remove"
              :disabled="!formControlEnabled"
              @click="deleteAdditionalInfoConfirm(index)"
            ></cs-button>
          </v-col>
        </v-row>
      </div>
    </div>
    <cs-form-dialog
      id="dlg-confirm-additional-info-list"
      v-model="deletionConfirmationDialogDisplayed"
      body-id="dlg-body-confirm-additional-info-list"
      heading="Remove Additional Information"
      :primary-action="{
        label: 'Remove',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click'
      }"
      @primary-click="deleteAdditionalInfo"
      @secondary1-click="deletionConfirmationDialogDisplayed = false"
    >
      <template #cs-form-dialog-content>
        You are about to remove additional information, would you like to continue?
      </template>
    </cs-form-dialog>
    <cs-form-dialog
      id="dlg-invalid-file-type"
      v-model="invalidFileTypeDialogDisplayed"
      body-id="dlg-body-invalid-file-type"
      heading="Upload Additional Information"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="invalidFileTypeDialogDisplayed = false"
    >
      <template #cs-form-dialog-content>
        Invalid file type:
        <span class="debug-error-message">{{ invalidFileTypeName }}</span> <br />
        Sorry, <span class="debug-error-message">{{ uploadFileType }}</span> file type is not
        supported.
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
export default {
  props: {
    formControlEnabled: {
      type: Boolean,
      required: true
    },
    additionalInformationItems: {
      type: Array,
      required: true
    }
  },
  emits: ['setAdditionalInformationValid'],
  data: () => ({
    deletionConfirmationDialogDisplayed: false,
    invalidFileTypeDialogDisplayed: false,
    invalidFileTypeName: '',
    selectedAdditional: {},
    fileTypeAccept: `.pdf,.csv,.doc,.docx,.xls,.xlsx,.txt,.odt,.ods,.odp,.png,.jpeg,.jpg`,
    fileMimeTypeAccept: [
      'application/pdf',
      'text/csv',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'application/vnd.oasis.opendocument.text',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.oasis.opendocument.presentation',
      'image/png',
      'image/jpeg',
      'image/jpg'
    ],
    uploadFileType: ''
  }),
  methods: {
    addAdditionalInfo() {
      const additionalInformationItemsAdded = this.additionalInformationItems.filter(
        (r) => !r.isDeleted
      );
      let showDeleteButton = true;
      if (additionalInformationItemsAdded.length === 0) {
        showDeleteButton = false;
      }

      // eslint-disable-next-line vue/no-mutating-props
      this.additionalInformationItems.push({
        fileSummary: '',
        comment: '',
        showDeleteButton
      });

      this.$forceUpdate();
      this.setAdditionalInformationValid();
    },
    deleteAdditionalInfoConfirm(index) {
      this.selectedAdditional.index = index;
      this.deletionConfirmationDialogDisplayed = true;
    },
    deleteAdditionalInfo() {
      const { index } = this.selectedAdditional;
      const items = this.additionalInformationItems;
      const item = this.additionalInformationItems[index];

      if (item.sid) {
        item.isDeleted = true;
      } else {
        items.splice(index, 1);
      }
      this.deleteAttachment(item);
      this.deletionConfirmationDialogDisplayed = false;
      return true;
    },
    async deleteAttachment(item) {
      const items = this.additionalInformationItems;
      const additionalInformationItemsAdded = items.filter((a) => {
        return !a.isDeleted;
      });

      if (additionalInformationItemsAdded.length === 0) {
        this.addAdditionalInfo();
      }

      const excursionId = this.$store.state.common.excursionDc.id;
      if (item.isDeleted) {
        const argHash = {
          queryParam: this.$store.state.common.apiParam,
          excursionId,
          attachmentSid: item.sid,
          store: this.$store
        };
        await this.$app.stbApiAdapter.deleteExcursionAttachments(argHash);
      }
      this.setAdditionalInformationValid();
    },
    getUploadFileId(index) {
      return `uploadFile_${index}`;
    },
    getUploadButtonId(index) {
      return `additional-info-list--upload-${index}`;
    },
    getDownloadFileId(index) {
      return `additional-info-list--download---button-${index}`;
    },
    getFileSummaryId(index) {
      return `additional-info-list--file-summary-${index}`;
    },
    getDescriptionId(index) {
      return `additional-info-list--description-${index}`;
    },
    getNotesId(index) {
      return `additional-info-list--notes--text-input-${index}`;
    },
    getAddFileId(index) {
      return `additional-info-list--add-file-${index}`;
    },
    getDeleteId(index) {
      return `additional-info-list--delete-${index}`;
    },
    uploadFileClick(index) {
      document.getElementById(`uploadFile_${index}`).click();
    },
    getUploadDate(uploadDate) {
      let timezoneDate = '';
      if (uploadDate) {
        const localDate = this.$app.moment(uploadDate);
        timezoneDate = this.$app
          .moment(localDate)
          .utc()
          .tz(this.$store.state.common.clientTimezone)
          .format('MM/DD/YYYY, hh:mm:ss A');
      }
      return timezoneDate;
    },
    handleTemplateFileChange(e, additional, index) {
      if (e && e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];
        const filename = file.name;
        const extensionType = filename.split('.').pop();
        const fileType = file.type;

        if (
          this.fileTypeAccept.indexOf(extensionType) < 0 ||
          !this.fileMimeTypeAccept.includes(fileType)
        ) {
          this.invalidFileTypeDialogDisplayed = true;
          this.uploadFileType = `.${extensionType}`;
          this.invalidFileTypeName = filename;
          e.target.value = '';
          return;
        }

        additional.file = file;
        additional.filename = file.name;
        additional.fileUpdateDate = '';
        this.$forceUpdate();
        this.additionalValueChange(additional, index);
        this.setAdditionalInformationValid();
      }
    },
    showAddFileButton(index) {
      const additionalInformationItemsAdded = this.additionalInformationItems.filter((a) => {
        return !a.isDeleted;
      });

      if (additionalInformationItemsAdded.length === 1) {
        return true;
      }

      // Allow uploading up to 50 files
      if (additionalInformationItemsAdded.length === 50) {
        return false;
      }

      for (let i = this.additionalInformationItems.length - 1; i > 0; i -= 1) {
        if (!this.additionalInformationItems[i].isDeleted) {
          if (index === i) {
            return true;
          }
          break;
        }
      }

      return false;
    },
    additionalValueChange(additional, index) {
      const localAdditional = additional;
      if (index === 0) {
        if (
          localAdditional.filename ||
          localAdditional.fileSummary ||
          localAdditional.comment ||
          localAdditional.notes
        ) {
          localAdditional.showDeleteButton = true;
        } else {
          localAdditional.showDeleteButton = false;
        }
      }
      if (localAdditional.notes && localAdditional.notes.length > 0) {
        localAdditional.comment = '';
      }
      this.$forceUpdate();
      localAdditional.isUpdated = true;
      this.setAdditionalInformationValid();
    },
    setAdditionalInformationValid() {
      const additionalInformationItemsAdded = this.additionalInformationItems.filter((a) => {
        return !a.isDeleted;
      });

      let additionalInformationValid = false;
      if (additionalInformationItemsAdded) {
        if (additionalInformationItemsAdded.length === 1) {
          const additional = additionalInformationItemsAdded[0];
          if (
            (additional.filename && additional.fileSummary) ||
            (!additional.filename && additional.fileSummary !== '' && additional.notes) ||
            (!additional.filename &&
              !additional.fileSummary &&
              !additional.comment &&
              !additional.notes)
          ) {
            additionalInformationValid = true;
          }
        } else {
          additionalInformationValid = additionalInformationItemsAdded.every((additional) => {
            return (
              (additional.filename && additional.fileSummary) ||
              (!additional.filename && additional.fileSummary !== '' && additional.notes)
            );
          });
        }
      }

      this.$emit('setAdditionalInformationValid', additionalInformationValid);
    },
    async downloadFile(additional) {
      const apiQuery = JSON.parse(JSON.stringify(this.$store.state.common.apiParam));

      const argHash = {
        queryParam: apiQuery,
        sid: additional.sid,
        store: this.$store
      };
      const response = await this.$app.stbApiAdapter.getAttachments(argHash);
      if (response.data) {
        const attachments = response.data;
        if (attachments.length > 0) {
          const file = attachments[0];

          if (file && file.computed && file.metadata) {
            const filePath = file.computed.url.replace(/^\//, '');
            const apiUrl = this.$app.env.API_URL.replace(/\/$/, '');
            const fileUrl = `${apiUrl}/${filePath}`;
            const { filename } = file.metadata;
            const excursionCommonAdapter = new this.$app.excursionCommon.Adapter({
              store: this.$store,
              app: this.$app
            });
            excursionCommonAdapter.downloadFile(fileUrl, filename, this.$app.deviceDetector);
          }
        }
      }
    },
    showFirstMandatory(additional) {
      if (additional.isDeleted) {
        return false;
      }

      const additionalInformationItemsAdded = this.additionalInformationItems.filter((a) => {
        return !a.isDeleted;
      });
      if (additionalInformationItemsAdded && additionalInformationItemsAdded.length === 1) {
        const additionalInfo = additionalInformationItemsAdded[0];
        if (
          additionalInfo.filename ||
          additionalInfo.fileSummary ||
          additionalInfo.comment ||
          additionalInfo.notes
        ) {
          return true;
        }

        return false;
      }

      return true;
    }
  }
};
</script>
<style scoped>
.additional-info-list {
  padding-left: 4px;
}

.template-file-font {
  font-size: 14px;
  margin: 8px 4px !important;
}

.additional-info-list .layout.wrap {
  padding-left: 4px !important;
}

.additional-info-list-i-content {
  font-family: 'Gilroy' !important;
}

.additional-info-list-i-content .v-icon {
  color: rgb(60, 55, 55, 0.54);
  padding-bottom: 2px;
  font-size: 16px;
}

.additional-info-list-info-text {
  padding-top: 18px;
  padding-bottom: 23px;
  color: rgb(0, 0, 0, 0.6);
}
</style>
